import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

/** Dias visíveis no painel unificado do dashboard (área de cliente). */
export const DASHBOARD_TIMELINE_DAYS = 365;

const KIND_PRIORITY = {
  appointment: 0,
  event: 1,
};

const STATE_LABELS = {
  approved: "Aprovado",
  pending: "Pendente",
  rejected: "Rejeitado",
  canceled: "Cancelado",
  "no-show": "Não compareceu",
  deleted: "Eliminado",
  done: "Realizado",
  "pending-payment": "Pagamento pendente",
  "approved-unpaid": "Aprovado",
};

const EVENT_TYPE_LABELS = {
  webinar: "Webinar",
  public: "Evento",
  conference: "Conferência",
};

export function buildDashboardDateRange(days = DASHBOARD_TIMELINE_DAYS) {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + days - 1);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

export function formatShortPeriodLabel(start, end) {
  const fmt = (d) => dayjs(d).format("DD/MM/YYYY");
  return `${fmt(start)} a ${fmt(end)}`;
}

export function toDateKey(date) {
  return dayjs(date).format("YYYY-MM-DD");
}

export function formatTimelineMonthLabel(date) {
  return date
    .toLocaleDateString("pt-PT", { month: "short" })
    .replace(/\.$/, "")
    .toUpperCase();
}

export function isDateWithinRange(date, start, end) {
  const d = dayjs(date);
  return (
    !d.isBefore(dayjs(start).startOf("day")) &&
    !d.isAfter(dayjs(end).endOf("day"))
  );
}

export function parseAppointmentDate(value) {
  if (!value) return null;
  const date = dayjs.utc(value).local().toDate();
  return Number.isNaN(date.getTime()) ? null : date;
}

export function getAppointmentStartDate(appointment) {
  if (!appointment) return null;
  return appointment.startDate || appointment.start || null;
}

export function formatAppointmentTime(appointment) {
  const startRaw = getAppointmentStartDate(appointment);
  if (!startRaw) return "—";
  const start = dayjs.utc(startRaw).local().format("HH:mm");
  const endRaw = appointment.endDate || appointment.end;
  if (!endRaw) return start;
  const end = dayjs.utc(endRaw).local().format("HH:mm");
  return end && end !== start ? `${start} – ${end}` : start;
}

export function parseEventDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const raw =
    typeof value === "object" && value.date ? value.date : String(value);
  const date = dayjs.utc(raw).local().toDate();
  return Number.isNaN(date.getTime()) ? null : date;
}

export function composeEventDateTime(startDate, startTime) {
  const date = parseEventDate(startDate);
  if (!date) return null;
  date.setHours(0, 0, 0, 0);
  if (startTime) {
    const raw =
      typeof startTime === "object" && startTime.date
        ? startTime.date
        : String(startTime);
    const match = raw.match(/(\d{1,2}):(\d{2})/);
    if (match) date.setHours(Number(match[1]), Number(match[2]), 0, 0);
  }
  return date;
}

export function formatEventTimeOnly(event) {
  const extract = (value) => {
    if (!value) return null;
    const raw =
      typeof value === "object" && value.date ? value.date : String(value);
    const match = raw.match(/(\d{1,2}):(\d{2})/);
    return match ? `${match[1].padStart(2, "0")}:${match[2]}` : null;
  };
  const start = extract(event.startTime);
  if (!start) return "—";
  const end = extract(event.endTime);
  if (end && end !== start) return `${start} – ${end}`;
  return start;
}

export function getEventTypeLabel(type) {
  return EVENT_TYPE_LABELS[type] || "Evento";
}

export function appointmentStateClass(state) {
  if (!state) return "unknown";
  return String(state).toLowerCase();
}

export function appointmentStatusLabel(state) {
  if (!state) return "Agendamento";
  const key = String(state).toLowerCase();
  return STATE_LABELS[key] || state;
}

export function getEventDaysLabel(event) {
  const date = composeEventDateTime(event.startDate, event.startTime);
  if (!date) return "";
  const diff = dayjs(date).startOf("day").diff(dayjs().startOf("day"), "day");
  if (diff > 0) return diff === 1 ? "Amanhã" : `Faltam ${diff} dias`;
  if (diff === 0) return "É hoje";
  return "";
}

export function isUpcomingWebinar(webinar) {
  if (!webinar || !webinar.startDate) return false;
  const now = dayjs();
  const start = dayjs.utc(webinar.startDate).local();
  const endDate = webinar.endDate || webinar.startDate;
  const end = dayjs.utc(endDate).local().endOf("day");
  if (end.isBefore(now)) return false;
  if (webinar.isRegistered) return true;
  if (typeof webinar.canRegister === "boolean") {
    return webinar.canRegister;
  }
  if (webinar.videoUrl && start.isBefore(now)) return false;
  return start.isAfter(now) || start.isSame(now, "minute");
}

export function isUpcomingPublicEvent(event) {
  if (!event || !event.startDate) return false;
  const now = dayjs();
  const endDate = event.endDate || event.startDate;
  const end = dayjs.utc(endDate).local().endOf("day");
  return !end.isBefore(now);
}

export function mapAppointmentsToItems(appointments) {
  return (appointments || [])
    .map((appointment) => {
      const date = parseAppointmentDate(getAppointmentStartDate(appointment));
      if (!date) return null;
      const id = appointment.id || appointment.appointmentId;
      if (!id) return null;
      return {
        kind: "appointment",
        id: `appointment-${id}`,
        date,
        data: appointment,
      };
    })
    .filter(Boolean);
}

export function mapEventsToItems(events) {
  return (events || [])
    .map((event) => {
      const date = composeEventDateTime(event.startDate, event.startTime);
      if (!date) return null;
      return {
        kind: "event",
        id: `event-${event.type || "event"}-${event.id}`,
        date,
        data: event,
      };
    })
    .filter(Boolean);
}

const itemDate = (item) =>
  item.date && item.date.getTime ? item.date.getTime() : 0;

export function buildUnifiedDayGroups(items, filter = "all") {
  const todayKey = toDateKey(new Date());
  const map = new Map();

  for (const item of items) {
    if (filter !== "all") {
      if (filter === "appointment" && item.kind !== "appointment") continue;
      if (filter === "webinar") {
        if (item.kind !== "event" || item.data.type !== "webinar") continue;
      }
      if (filter === "event") {
        if (
          item.kind !== "event" ||
          (item.data.type !== "public" && item.data.type !== "conference")
        )
          continue;
      }
    }
    if (!item.date) continue;
    const key = toDateKey(item.date);
    const list = map.get(key) || [];
    list.push(item);
    map.set(key, list);
  }

  return Array.from(map.entries())
    .map(([dateKey, dayItems]) => {
      const sorted = [...dayItems].sort((a, b) => {
        const pr = KIND_PRIORITY[a.kind] - KIND_PRIORITY[b.kind];
        if (pr !== 0) return pr;
        return itemDate(a) - itemDate(b);
      });
      const date = sorted[0].date;
      const isToday = dateKey === todayKey;
      let dotVariant = "upcoming";
      if (isToday) dotVariant = "today";

      return {
        dateKey,
        dayNumber: String(date.getDate()).padStart(2, "0"),
        monthLabel: formatTimelineMonthLabel(date),
        weekdayLabel: date.toLocaleDateString("pt-PT", { weekday: "long" }),
        isToday,
        dotVariant,
        items: sorted,
      };
    })
    .sort((a, b) => a.dateKey.localeCompare(b.dateKey));
}
